import React from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { Box, Typography } from 'app/shared/components/atoms';
import { IconButton } from 'app/shared/components/molecules/IconButton';
import { ReactComponent as Minus } from 'icons/ticket-stepper-minus.svg';
import { ReactComponent as MinusDisabled } from 'icons/ticket-stepper-minus-disabled.svg';
import { ReactComponent as Plus } from 'icons/ticket-stepper-plus.svg';
import { ReactComponent as PlusDisabled } from 'icons/ticket-stepper-plus-disabled.svg';

interface StepperProps {
  'data-qaid'?: string;
  numberOfTickets: number;
  setNumberOfTickets: (numberOfTickets: number) => void;
  canRedeemAndIsRedeemingVisaFreeTicket?: boolean;
  stepperDeclineVisaFreeTicketAction?: () => void;
  showMaxTicketsDisclaimer?: boolean;
  maxTickets: number;
  minTickets?: number;
  disabled?: boolean;
}

export const TicketQuantityStepper: React.FC<
  React.PropsWithChildren<StepperProps>
> = ({
  'data-qaid': qaId,
  numberOfTickets,
  setNumberOfTickets,
  canRedeemAndIsRedeemingVisaFreeTicket,
  stepperDeclineVisaFreeTicketAction,
  maxTickets,
  minTickets = 1,
  disabled = false,
}) => {
  const { formatMessage } = useIntl();
  const { colors } = useCurrentTheme();

  const onStep = (number: number) => {
    if (
      canRedeemAndIsRedeemingVisaFreeTicket &&
      number === 1 &&
      stepperDeclineVisaFreeTicketAction
    ) {
      stepperDeclineVisaFreeTicketAction();
    } else {
      setNumberOfTickets(number);
    }
  };

  const decreaseButtonDisabled = numberOfTickets <= minTickets || disabled;
  const increaseButtonDisabled = numberOfTickets >= maxTickets || disabled;

  const ticketDecreaseIcon = decreaseButtonDisabled ? MinusDisabled : Minus;

  const ticketIncreaseIcon = increaseButtonDisabled ? PlusDisabled : Plus;

  return (
    <Box
      flexDirection="column"
      alignItems="center"
      data-qaid="ticket-quantity-stepper"
      gap={4}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        gap={2}
        justifyContent="space-between"
      >
        <IconButton
          backgroundColor="transparent"
          useNativeColors
          disabled={decreaseButtonDisabled}
          onClick={() => onStep(numberOfTickets - 1)}
          data-qaid={`${qaId}-dec`}
          icon={ticketDecreaseIcon}
          size="lg"
        />
        <Typography
          fontSize="fontSize500"
          lineHeight="100%"
          color={colors.primary.black.base}
          m={0}
          p={0}
          fontWeight="bold"
          data-qaid="ticket-quantity-number-of-tickets"
        >
          {numberOfTickets}
        </Typography>
        <IconButton
          backgroundColor="transparent"
          useNativeColors
          disabled={increaseButtonDisabled}
          onClick={() => onStep(numberOfTickets + 1)}
          data-qaid={`${qaId}-inc`}
          icon={ticketIncreaseIcon}
          size="lg"
        />
      </Box>
      <Box flexDirection="row">
        <Typography
          pt={2}
          color={colors.primary.black.base}
          fontSize="fontSize75"
          data-qaid="ticket-quantity-limit"
        >
          {formatMessage({ id: 'event.ticketCounter.limit' })}
          {maxTickets}
        </Typography>
      </Box>
    </Box>
  );
};
